import type { StateCreator } from 'zustand';
import type { Position, Stats } from '../../types';
import { CHART_LEN } from '../../constants';
import { persist } from '../persist';
import type { GameState } from '../types';
import { haptic } from '../../sdk/haptic';
import { submitScore } from '../../sdk/leaderboard';

export interface TradeSlice {
  position: Position | null;
  presets: [number, number, number];
  presetIdx: 0 | 1 | 2 | 3;
  customPct: number;
  cooldown: boolean;

  doBuy: () => void;
  doSell: () => void;
  selectPreset: (idx: 0 | 1 | 2 | 3) => void;
  setCustomPct: (pct: number) => void;
}

function activePct(s: GameState): number {
  return s.presetIdx === 3 ? s.customPct : s.presets[s.presetIdx];
}

export const createTradeSlice: StateCreator<GameState, [], [], TradeSlice> = (set, get) => ({
  position: null,
  presets: [10, 50, 100],
  presetIdx: 0,
  customPct: 25,
  cooldown: false,

  doBuy: () => {
    const s = get();
    if (s.position || s.cooldown || s.cash <= 0) return;
    const pct = activePct(s);
    const amount = Math.floor(s.cash * (pct / 100));
    if (amount <= 0) return;

    const position: Position = {
      symbol: s.sym,
      leverage: s.lev,
      entryPrice: s.prices[s.sym].at(-1)!,
      amount,
      boosted: s.boostActive,
    };
    set({
      position,
      cash: s.cash - amount,
      boostActive: false,
      cooldown: true,
    });
    haptic('buy');
    setTimeout(() => set({ cooldown: false }), 1000);

    // 튜토리얼 step 1: 즉시 +5% 후 step 2
    if (s.tutStep === 1) {
      const next = { ...get().prices, [s.sym]: [...get().prices[s.sym]] };
      next[s.sym].push(next[s.sym].at(-1)! * 1.05);
      if (next[s.sym].length > CHART_LEN + 1) next[s.sym].shift();
      set({ prices: next });
      setTimeout(() => get().setTutStep(2), 600);
      get().showToast('사재기 완료!');
      persist(get());
      return;
    }

    get().showToast('사재기 완료!');
    persist(get());
  },

  doSell: () => {
    const s = get();
    if (!s.position) return;
    const p = s.position;
    const cur = s.prices[p.symbol].at(-1)!;
    const rawPct = (cur - p.entryPrice) / p.entryPrice;
    let levPct = rawPct * p.leverage;
    if (p.boosted) levPct = levPct > 0 ? levPct * 2 : levPct * 0.5;

    const proceeds = Math.max(Math.floor(p.amount * (1 + levPct)), 0);
    const pnl = proceeds - p.amount;

    const newRecord = levPct * 100 > s.stats.maxRet && pnl > 0;
    const newStats: Stats = {
      ...s.stats,
      totalTrades: s.stats.totalTrades + 1,
      maxRet: Math.max(s.stats.maxRet, levPct * 100),
      streak: pnl > 0 ? s.stats.streak + 1 : 0,
      maxStreak: pnl > 0 ? Math.max(s.stats.maxStreak, s.stats.streak + 1) : s.stats.maxStreak,
      maxCash: Math.max(s.stats.maxCash, s.cash + proceeds),
    };
    set({
      cash: s.cash + proceeds,
      position: null,
      stats: newStats,
    });

    // 햅틱 — 수익/손실/신기록
    if (newRecord)      haptic('rankUp');
    else if (pnl >= 0)  haptic('sellWin');
    else                haptic('sellLoss');

    const sign = pnl >= 0 ? '+' : '';
    if (newRecord) {
      get().showToast(`🏆 한 방 신기록! ${sign}${(levPct * 100).toFixed(2)}%`);
    } else {
      get().showToast(`물량풀기 ${sign}${Math.round(pnl).toLocaleString('ko-KR')}냥`);
    }

    // 매도 시점 자산을 리더보드에 제출 (fire-and-forget)
    void submitScore(get().cash);

    if (s.tutStep === 2) setTimeout(() => get().setTutStep(3), 400);
    persist(get());
  },

  selectPreset: (idx) => {
    const s = get();
    if (s.position) return;
    if (idx === 3) { set({ showPctOverlay: true }); return; }
    set({ presetIdx: idx });
  },

  setCustomPct: (pct) => {
    if (pct >= 1 && pct <= 100) {
      set({ customPct: pct, presetIdx: 3, showPctOverlay: false });
    } else {
      set({ showPctOverlay: false });
    }
  },
});
